// Client-side pager for the author page's attributed-article list.
//
// Used in "all blogs" mode, where every matching card is already in the DOM:
// paging is a pure show/hide of a slice, so switching pages costs no request
// and never reloads. Cards arrive grouped by blog, so they are sorted
// newest-first here using each card's data-date (a unix timestamp).
//
// The list keeps the height of a full page so short pages (the last one) can't
// collapse the layout and move the pagination under the cursor.
class AuthorPostsPager extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector("[data-author-posts]");
    this.nav = this.querySelector("[data-author-pagination]");
    this.perPage = parseInt(this.getAttribute("data-per-page") || "12", 10);
    if (!this.list || !this.nav) return;

    this.cards = [...this.list.querySelectorAll("a.post")];
    if (this.cards.length === 0) return;

    // Newest first across all blogs.
    this.cards
      .slice()
      .sort((a, b) => (parseInt(b.dataset.date || "0", 10) - parseInt(a.dataset.date || "0", 10)))
      .forEach((card) => this.list.appendChild(card));
    this.cards = [...this.list.querySelectorAll("a.post")];

    this.pages = Math.ceil(this.cards.length / this.perPage);
    this.current = 1;
    this.reserveHeight();
    this.render(1);

    this.nav.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-page]");
      if (!btn) return;
      e.preventDefault();
      const page = parseInt(btn.getAttribute("data-page"), 10);
      if (!page || page === this.current || page < 1 || page > this.pages) return;
      this.swap(page);
    });
  }

  // Reserve the tallest full page so shorter pages don't shrink the container.
  reserveHeight() {
    this.list.style.minHeight = "";
    const full = Math.min(this.perPage, this.cards.length);
    this.cards.forEach((c, i) => { c.style.display = i < full ? "" : "none"; });
    const h = this.list.offsetHeight;
    if (h) this.list.style.minHeight = h + "px";
  }

  render(page) {
    const start = (page - 1) * this.perPage;
    const end = start + this.perPage;
    this.cards.forEach((c, i) => { c.style.display = i >= start && i < end ? "" : "none"; });
    this.current = page;
    this.renderNav();
  }

  swap(page) {
    this.list.style.opacity = "0";
    setTimeout(() => {
      this.render(page);
      void this.list.offsetWidth; // force reflow so the fade-in animates
      this.list.style.opacity = "1";
    }, 200);
  }

  // Windowed nav (first … current-1 current current+1 … last), matching the
  // markup of snippets/article-pagination.liquid.
  renderNav() {
    if (this.pages <= 1) { this.nav.innerHTML = ""; return; }
    const cell =
      "inline-flex items-center justify-center min-w-[40px] h-10 px-3 rounded-lg border border-black border-opacity-10 lg:hover:bg-black lg:hover:text-white transition-colors duration-200";
    const parts = [];
    if (this.current > 1) {
      parts.push('<a href="#" data-page="' + (this.current - 1) + '" rel="prev" aria-label="Previous page" class="' + cell + '">&larr;</a>');
    }
    let prev = 0;
    for (let n = 1; n <= this.pages; n++) {
      const inWindow = n === 1 || n === this.pages || Math.abs(n - this.current) <= 1;
      if (!inWindow) continue;
      if (prev && n - prev > 1) {
        parts.push('<span class="inline-flex items-center justify-center min-w-[28px] h-10 opacity-40" aria-hidden="true">&hellip;</span>');
      }
      if (n === this.current) {
        parts.push('<span aria-current="page" class="inline-flex items-center justify-center min-w-[40px] h-10 px-3 rounded-lg bg-black text-white">' + n + "</span>");
      } else {
        parts.push('<a href="#" data-page="' + n + '" class="' + cell + '">' + n + "</a>");
      }
      prev = n;
    }
    if (this.current < this.pages) {
      parts.push('<a href="#" data-page="' + (this.current + 1) + '" rel="next" aria-label="Next page" class="' + cell + '">&rarr;</a>');
    }
    this.nav.innerHTML = parts.join("");
  }
}

customElements.define("author-posts-pager", AuthorPostsPager);
